import React, { useState } from "react";
import styled from "styled-components";
import { Coach as CoachModel } from "../model/Coach";
import { Division as DivisionModel, Week as WeekModel } from "../model/Season";

const ScheduleTableSection = styled.div`
    margin-top: 1em;
`;

const ScheduleTableContainer = styled.div`
    overflow-x: auto;
    margin-top: 0.5em;
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: white;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
`;

const ScheduleTable = styled.div`
    display: grid;
    min-width: 800px;
`;

const TableHeader = styled.div`
    display: contents;
`;

const TableHeaderCell = styled.div`
    background-color: #f8f9fa;
    padding: 0.125em 0.5em;
    border-bottom: 1px solid #ddd;
    border-right: 1px solid #ddd;
    font-weight: 600;
    text-align: center;
    font-size: 0.85em;
    color: #333;
    
    &:first-child {
        border-left: 1px solid #ddd;
        text-align: left;
        min-width: 200px;
    }
`;

const TableRow = styled.div`
    display: contents;
`;

const TableCell = styled.div`
    padding: 0.125em 0.5em;
    border-bottom: 1px solid #e9ecef;
    border-right: 1px solid #e9ecef;
    font-size: 0.85em;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:first-child {
        border-left: 1px solid #e9ecef;
        text-align: left;
        justify-content: flex-start;
        font-weight: 500;
        background-color: #f8f9fa;
    }
`;

const TableDivisionHeader = styled.div`
    background-color: #e9ecef;
    padding: 0.35em 0.6em;
    font-weight: 600;
    color: #495057;
    font-size: 0.85em;
    border-bottom: 1px solid #dee2e6;
    border-right: 1px solid #dee2e6;
    border-left: 1px solid #dee2e6;
    grid-column: 1 / -1;
`;

const MatchCell = styled.div<{ $hasResults: boolean; $isClickable: boolean; $isCompleted: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.15em;
    text-align: center;
    font-size: 0.75em;
    line-height: 1.1;
    cursor: ${props => props.$isClickable ? 'pointer' : 'default'};
    background-color: ${props => props.$isCompleted ? '#f8f9fa' : 'transparent'};
    border-radius: 4px;
    padding: ${props => props.$isCompleted ? '0.25em' : '0'};
    
    &:hover {
        ${props => props.$isClickable && `
            background-color: #e9ecef;
            border-radius: 4px;
            padding: 0.25em;
            margin: -0.25em;
        `}
    }
`;

const MatchText = styled.div<{ $isWin?: boolean; $isLoss?: boolean; $isTie?: boolean }>`
    font-weight: 500;
    font-size: 0.9em;
    color: ${props => {
        if (props.$isWin) return '#28a745';
        if (props.$isLoss) return '#dc3545';
        if (props.$isTie) return '#6c757d';
        return '#333';
    }};
`;

const MatchResult = styled.div<{ $hasResults: boolean }>`
    font-size: 0.7em;
    color: ${props => props.$hasResults ? '#28a745' : '#6c757d'};
    font-weight: ${props => props.$hasResults ? '600' : '400'};
`;

const EmptyCell = styled.div`
    color: #6c757d;
    font-style: italic;
    font-size: 0.75em;
`;

const TableResultsModal = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const TableResultsModalContent = styled.div`
    background-color: white;
    border-radius: 8px;
    padding: 2em;
    max-width: 500px;
    width: 90%;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const TableResultsModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5em;
    padding-bottom: 1em;
    border-bottom: 1px solid #e9ecef;
`;

const TableResultsModalTitle = styled.h3`
    margin: 0;
    color: #333;
`;

const TableResultsModalClose = styled.button`
    background: none;
    border: none;
    font-size: 1.5em;
    cursor: pointer;
    color: #666;
    
    &:hover {
        color: #333;
    }
`;

const TableResultsFormGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: 1.5em;
    align-items: center;
    margin-bottom: 1.5em;
`;

const TableTeamColumn = styled.div`
    text-align: center;
`;

const TableTeamName = styled.div`
    font-weight: 600;
    margin-bottom: 1em;
    color: #333;
    font-size: 1.1em;
`;

const TableScoreInput = styled.input`
    padding: 0.75em;
    border: 1px solid #ccc;
    border-radius: 4px;
    width: 100px;
    text-align: center;
    font-size: 1.2em;
    font-weight: 600;
    
    &:disabled {
        background-color: #f8f9fa;
        color: #495057;
        cursor: not-allowed;
        opacity: 0.8;
    }
`;

const TableCasInput = styled.input`
    padding: 0.75em;
    border: 1px solid #ccc;
    border-radius: 4px;
    width: 80px;
    text-align: center;
    
    &:disabled {
        background-color: #f8f9fa;
        color: #495057;
        cursor: not-allowed;
        opacity: 0.8;
    }
`;

const TableVersusText = styled.div`
    font-size: 1.5em;
    font-weight: 600;
    color: #666;
    text-align: center;
`;

const TableOvertimeSection = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5em;
    margin-top: 1.5em;
    padding-top: 1.5em;
    border-top: 1px solid #e9ecef;
`;

const TableOvertimeCheckbox = styled.input`
    margin: 0;
    transform: scale(1.2);
`;

const TableOvertimeLabel = styled.label`
    font-weight: 500;
    color: #666;
    font-size: 1.1em;
`;

const TableResultsMetadata = styled.div`
    margin-top: 1.5em;
    padding: 0.75em;
    background-color: #f8f9fa;
    border-radius: 4px;
    border: 1px solid #dee2e6;
`;

const TableResultsMetadataLabel = styled.div`
    font-size: 0.85em;
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.5em;
`;

const TableResultsMetadataText = styled.div`
    font-size: 0.8em;
    color: #6c757d;
    display: flex;
    flex-direction: column;
    gap: 0.25em;
    
    span {
        display: block;
    }
    
    strong {
        color: #495057;
        margin-right: 0.5em;
    }
`;

const TableResultsActions = styled.div`
    display: flex;
    gap: 1em;
    margin-top: 2em;
    justify-content: flex-end;
`;

const TableResultsButton = styled.button`
    background-color: #28a745;
    color: white;
    border: none;
    padding: 0.75em 1.5em;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1em;
    
    &:hover {
        background-color: #218838;
    }
    
    &:disabled {
        background-color: #6c757d;
        cursor: not-allowed;
    }
`;

const TableCancelButton = styled.button`
    background-color: #6c757d;
    color: white;
    border: none;
    padding: 0.75em 1.5em;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1em;
    
    &:hover {
        background-color: #545b62;
    }
`;

const Label = styled.label`
    font-size: 0.9em;
    color: #666;
    font-weight: 500;
`;

const WeekProgressContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25em;
`;

const WeekName = styled.div`
    font-weight: 600;
    font-size: 0.85em;
    color: #333;
`;

const WeekProgress = styled.div`
    display: flex;
    align-items: center;
    gap: 0.25em;
    font-size: 0.7em;
    color: #666;
`;

const ProgressBar = styled.div<{ $percentage: number }>`
    width: 40px;
    height: 4px;
    background-color: #e9ecef;
    border-radius: 2px;
    overflow: hidden;
    
    &::after {
        content: '';
        display: block;
        height: 100%;
        width: ${props => props.$percentage}%;
        background-color: ${props => props.$percentage === 100 ? '#28a745' : '#17a2b8'};
        transition: width 0.3s ease;
    }
`;

const CompletionIcon = styled.span<{ $completed: boolean }>`
    font-size: 0.8em;
    color: ${props => props.$completed ? '#28a745' : '#6c757d'};
`;

const TeamNameContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.15em;
`;

const TeamNameText = styled.div`
    font-weight: 600;
    font-size: 0.95em;
    color: #333;
    line-height: 1.2;
`;

const CoachNameText = styled.div`
    font-style: italic;
    font-size: 0.8em;
    color: #666;
    line-height: 1.2;
`;

interface SeasonScheduleProps {
    seasonData: {
        divisions: DivisionModel[];
        weeks: WeekModel[];
    };
    leagueCoaches: CoachModel[];
    onResultsUpdate?: (weekIndex: number, matchIndex: number, results: any) => void;
}

export const SeasonSchedule: React.FC<SeasonScheduleProps> = ({
    seasonData,
    leagueCoaches,
    onResultsUpdate
}) => {
    // Validate and normalize data structure
    const safeSeasonData = React.useMemo(() => {
        if (!seasonData) {
            return { divisions: [], weeks: [] };
        }
        return {
            divisions: Array.isArray(seasonData.divisions) ? seasonData.divisions : [],
            weeks: Array.isArray(seasonData.weeks) ? seasonData.weeks : []
        };
    }, [seasonData]);

    const safeLeagueCoaches = React.useMemo(() => {
        return Array.isArray(leagueCoaches) ? leagueCoaches : [];
    }, [leagueCoaches]);

    const [showTableResultsModal, setShowTableResultsModal] = useState<{
        weekIndex: number;
        matchIndex: number;
        teamId: string;
    } | null>(null);
    const [resultsFormData, setResultsFormData] = useState({
        homeTeamScore: 0,
        awayTeamScore: 0,
        homeTeamCasInflicted: 0,
        awayTeamCasInflicted: 0,
        overtime: false
    });

    // Get all available teams for match creation
    const getAllTeams = () => {
        const teams: { teamId: string; teamName: string; race: string; coachName: string; divisionName: string }[] = [];
        try {
            safeSeasonData.divisions.forEach(division => {
                if (!division || !Array.isArray(division.teams)) return;
                division.teams.forEach(team => {
                    if (!team || !team.teamId) return;
                    const coach = safeLeagueCoaches.find(c => c && c.coachId === team.coachId);
                    teams.push({
                        teamId: team.teamId || 'unknown',
                        teamName: team.teamName || 'Unknown Team',
                        race: team.race || 'Unknown',
                        coachName: coach ? coach.coachName : `Coach ${team.coachId || 'unknown'}`,
                        divisionName: division.divisionName || 'Unknown Division'
                    });
                });
            });
        } catch (error) {
            console.error('Error getting teams:', error);
        }
        return teams;
    };

    const getMatchForTeam = (teamId: string, weekIndex: number) => {
        try {
            if (!safeSeasonData.weeks || weekIndex < 0 || weekIndex >= safeSeasonData.weeks.length) {
                return null;
            }
            const week = safeSeasonData.weeks[weekIndex];
            if (!week || !Array.isArray(week.matches)) return null;
            
            const match = week.matches.find(m => m && (m.homeTeamId === teamId || m.awayTeamId === teamId));
            if (!match) return null;
            
            // Find the home and away teams
            const homeTeam = safeSeasonData.divisions
                .flatMap(div => (div && Array.isArray(div.teams) ? div.teams : []))
                .find(team => team && team.teamId === match.homeTeamId);
            const awayTeam = safeSeasonData.divisions
                .flatMap(div => (div && Array.isArray(div.teams) ? div.teams : []))
                .find(team => team && team.teamId === match.awayTeamId);
            
            const homeCoach = safeLeagueCoaches.find(c => c && c.coachId === homeTeam?.coachId);
            const awayCoach = safeLeagueCoaches.find(c => c && c.coachId === awayTeam?.coachId);
            const isHome = match.homeTeamId === teamId;
            
            const opponentTeam = isHome ? awayTeam : homeTeam;
            const opponentCoach = isHome ? awayCoach : homeCoach;
            const opponent = opponentTeam && opponentCoach 
                ? `${opponentTeam.teamName || 'Unknown'} - ${opponentCoach.coachName}`
                : `Coach ${isHome ? (match.awayTeamId || 'unknown') : (match.homeTeamId || 'unknown')}`;
            
            return {
                match,
                isHome,
                opponent,
                hasResults: match.results !== undefined && match.results !== null
            };
        } catch (error) {
            console.error('Error getting match for team:', error);
            return null;
        }
    };

    // Calculate week progress
    const getWeekProgress = (weekIndex: number) => {
        try {
            if (!safeSeasonData.weeks || weekIndex < 0 || weekIndex >= safeSeasonData.weeks.length) {
                return { completed: 0, total: 0, percentage: 0 };
            }
            const week = safeSeasonData.weeks[weekIndex];
            if (!week || !Array.isArray(week.matches) || week.matches.length === 0) {
                return { completed: 0, total: 0, percentage: 0 };
            }
            
            const completed = week.matches.filter(match => match && match.results !== undefined && match.results !== null).length;
            const total = week.matches.length;
            const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
            
            return { completed, total, percentage };
        } catch (error) {
            console.error('Error calculating week progress:', error);
            return { completed: 0, total: 0, percentage: 0 };
        }
    };

    // Determine win/loss status for a team in a match
    const getMatchResultForTeam = (teamId: string, match: any) => {
        try {
            if (!match || !match.results) return null;
            
            const isHome = match.homeTeamId === teamId;
            const teamScore = isHome ? (match.results.homeTeamScore ?? 0) : (match.results.awayTeamScore ?? 0);
            const opponentScore = isHome ? (match.results.awayTeamScore ?? 0) : (match.results.homeTeamScore ?? 0);
            
            if (teamScore > opponentScore) return 'win';
            if (teamScore < opponentScore) return 'loss';
            return 'tie';
        } catch (error) {
            console.error('Error getting match result:', error);
            return null;
        }
    };

    // Table results modal handlers
    const handleShowTableResultsModal = (weekIndex: number, matchIndex: number, teamId: string) => {
        try {
            if (!safeSeasonData.weeks || weekIndex < 0 || weekIndex >= safeSeasonData.weeks.length) {
                return;
            }
            const week = safeSeasonData.weeks[weekIndex];
            if (!week || !Array.isArray(week.matches) || matchIndex < 0 || matchIndex >= week.matches.length) {
                return;
            }
            const match = week.matches[matchIndex];
            if (!match) return;

            if (match.results) {
                setResultsFormData({
                    homeTeamScore: match.results.homeTeamScore ?? 0,
                    awayTeamScore: match.results.awayTeamScore ?? 0,
                    homeTeamCasInflicted: match.results.homeTeamCasInflicted ?? 0,
                    awayTeamCasInflicted: match.results.awayTeamCasInflicted ?? 0,
                    overtime: match.results.overtime ?? false
                });
            } else {
                setResultsFormData({
                    homeTeamScore: 0,
                    awayTeamScore: 0,
                    homeTeamCasInflicted: 0,
                    awayTeamCasInflicted: 0,
                    overtime: false
                });
            }
            setShowTableResultsModal({ weekIndex, matchIndex, teamId });
        } catch (error) {
            console.error('Error showing results modal:', error);
        }
    };

    const handleSaveTableResults = () => {
        if (!showTableResultsModal) return;
        
        const { weekIndex, matchIndex } = showTableResultsModal;
        onResultsUpdate?.(weekIndex, matchIndex, resultsFormData);
        setShowTableResultsModal(null);
    };

    const handleCancelTableResults = () => {
        setShowTableResultsModal(null);
        setResultsFormData({
            homeTeamScore: 0,
            awayTeamScore: 0,
            homeTeamCasInflicted: 0,
            awayTeamCasInflicted: 0,
            overtime: false
        });
    };

    return (
        <ScheduleTableSection>
            {safeSeasonData.divisions.length > 0 && safeSeasonData.weeks.length > 0 ? (
                <ScheduleTableContainer>
                    <ScheduleTable style={{
                        gridTemplateColumns: `200px repeat(${safeSeasonData.weeks.length}, 1fr)`
                    }}>
                        <TableHeader>
                            <TableHeaderCell>Team</TableHeaderCell>
                            {safeSeasonData.weeks.map((week, weekIndex) => {
                                const progress = getWeekProgress(weekIndex);
                                return (
                                    <TableHeaderCell key={weekIndex}>
                                        <WeekProgressContainer>
                                            <WeekName>{week.weekName}</WeekName>
                                            {progress.total > 0 && (
                                                <WeekProgress>
                                                    <ProgressBar $percentage={progress.percentage} />
                                                    <span>{progress.completed}/{progress.total}</span>
                                                    <CompletionIcon $completed={progress.completed === progress.total}>
                                                        {progress.completed === progress.total ? '✅' : '⏳'}
                                                    </CompletionIcon>
                                                </WeekProgress>
                                            )}
                                        </WeekProgressContainer>
                                    </TableHeaderCell>
                                );
                            })}
                        </TableHeader>
                        
                        {(() => {
                            const teams = getAllTeams();
                            const teamsByDivision = teams.reduce((acc, team) => {
                                if (!acc[team.divisionName]) {
                                    acc[team.divisionName] = [];
                                }
                                acc[team.divisionName].push(team);
                                return acc;
                            }, {} as Record<string, typeof teams>);
                            
                            return Object.entries(teamsByDivision).map(([divisionName, divisionTeams]) => (
                                <React.Fragment key={divisionName}>
                                    <TableDivisionHeader>
                                        {divisionName}
                                    </TableDivisionHeader>
                                    {divisionTeams.map((team) => (
                                        <TableRow key={team.teamId}>
                                            <TableCell>
                                                <TeamNameContainer>
                                                    <TeamNameText>{team.teamName}</TeamNameText>
                                                    <CoachNameText>{team.coachName}</CoachNameText>
                                                </TeamNameContainer>
                                            </TableCell>
                                                                        {seasonData.weeks.map((_, weekIndex) => {
                                const matchInfo = getMatchForTeam(team.teamId, weekIndex);
                                                return (
                                                    <TableCell key={weekIndex}>
                                                        {matchInfo ? (
                                                            <MatchCell 
                                                                $hasResults={matchInfo.hasResults}
                                                                $isClickable={!!onResultsUpdate || matchInfo.hasResults}
                                                                $isCompleted={matchInfo.hasResults}
                                                                onClick={(onResultsUpdate || matchInfo.hasResults) ? () => {
                                                                    try {
                                                                        if (!safeSeasonData.weeks || weekIndex < 0 || weekIndex >= safeSeasonData.weeks.length) return;
                                                                        const week = safeSeasonData.weeks[weekIndex];
                                                                        if (!week || !Array.isArray(week.matches)) return;
                                                                        const matchIndex = week.matches.findIndex(m => 
                                                                            m && (m.homeTeamId === team.teamId || m.awayTeamId === team.teamId)
                                                                        );
                                                                        if (matchIndex !== -1) {
                                                                            handleShowTableResultsModal(weekIndex, matchIndex, team.teamId);
                                                                        }
                                                                    } catch (error) {
                                                                        console.error('Error handling match click:', error);
                                                                    }
                                                                } : undefined}
                                                            >
                                                                <MatchText 
                                                                    $isWin={getMatchResultForTeam(team.teamId, matchInfo.match) === 'win'}
                                                                    $isLoss={getMatchResultForTeam(team.teamId, matchInfo.match) === 'loss'}
                                                                    $isTie={getMatchResultForTeam(team.teamId, matchInfo.match) === 'tie'}
                                                                >
                                                                    {matchInfo.isHome ? 'vs' : '@'} {matchInfo.opponent}
                                                                </MatchText>
                                                                {matchInfo.hasResults && matchInfo.match.results && (
                                                                    <MatchResult $hasResults={true}>
                                                                        {matchInfo.isHome 
                                                                            ? `${matchInfo.match.results.awayTeamScore ?? 0}-${matchInfo.match.results.homeTeamScore ?? 0}`
                                                                            : `${matchInfo.match.results.awayTeamScore ?? 0}-${matchInfo.match.results.homeTeamScore ?? 0}`
                                                                        }
                                                                        {matchInfo.match.results.overtime && ' OT'}
                                                                    </MatchResult>
                                                                )}
                                                                {!matchInfo.hasResults && (
                                                                    <MatchResult $hasResults={false}>
                                                                        No results
                                                                    </MatchResult>
                                                                )}
                                                            </MatchCell>
                                                        ) : (
                                                            <EmptyCell>
                                                                Bye
                                                            </EmptyCell>
                                                        )}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))}
                                </React.Fragment>
                            ));
                        })()}
                    </ScheduleTable>
                </ScheduleTableContainer>
            ) : (
                <p>Add divisions and weeks to see the schedule table.</p>
            )}

            {/* Table Results Modal */}
            {showTableResultsModal && (() => {
                try {
                    const { weekIndex, matchIndex } = showTableResultsModal;
                    if (!safeSeasonData.weeks || weekIndex < 0 || weekIndex >= safeSeasonData.weeks.length) {
                        return null;
                    }
                    const week = safeSeasonData.weeks[weekIndex];
                    if (!week || !Array.isArray(week.matches) || matchIndex < 0 || matchIndex >= week.matches.length) {
                        return null;
                    }
                    const match = week.matches[matchIndex];
                    if (!match) return null;
                    
                    // Find teams from season data using teamId
                    const homeTeam = safeSeasonData.divisions
                        .flatMap(div => (div && Array.isArray(div.teams) ? div.teams : []))
                        .find(team => team && team.teamId === match.homeTeamId);
                    const awayTeam = safeSeasonData.divisions
                        .flatMap(div => (div && Array.isArray(div.teams) ? div.teams : []))
                        .find(team => team && team.teamId === match.awayTeamId);
                    
                    // Find coaches using the coachId from the teams
                    const homeCoach = homeTeam ? safeLeagueCoaches.find(c => c && c.coachId === homeTeam.coachId) : null;
                    const awayCoach = awayTeam ? safeLeagueCoaches.find(c => c && c.coachId === awayTeam.coachId) : null;
                
                // Build display names: prefer team name with coach name, fallback to team name, then coach name, then teamId
                const homeDisplayName = homeTeam && homeCoach
                    ? `${homeTeam.teamName} - ${homeCoach.coachName}`
                    : homeTeam
                    ? `${homeTeam.teamName}`
                    : homeCoach
                    ? homeCoach.coachName
                    : `Coach ${match.homeTeamId}`;
                    
                const awayDisplayName = awayTeam && awayCoach
                    ? `${awayTeam.teamName} - ${awayCoach.coachName}`
                    : awayTeam
                    ? `${awayTeam.teamName}`
                    : awayCoach
                    ? awayCoach.coachName
                    : `Coach ${match.awayTeamId}`;

                console.log("Here's the match:", match);
                
                    return (
                    <TableResultsModal onClick={handleCancelTableResults}>
                        <TableResultsModalContent onClick={(e) => e.stopPropagation()}>
                            <TableResultsModalHeader>
                                <TableResultsModalTitle>
                                    {onResultsUpdate ? 'Enter Results' : 'Match Results'} - {week.weekName}
                                </TableResultsModalTitle>
                                <TableResultsModalClose onClick={handleCancelTableResults}>
                                    ×
                                </TableResultsModalClose>
                            </TableResultsModalHeader>
                            
                            <TableResultsFormGrid>
                                <TableTeamColumn>
                                    <TableTeamName>{awayDisplayName}</TableTeamName>
                                    <div>
                                        <Label>Score:</Label>
                                        <TableScoreInput
                                            type="number"
                                            min="0"
                                            max="10"
                                            value={resultsFormData.awayTeamScore}
                                            onChange={(e) => setResultsFormData({
                                                ...resultsFormData,
                                                awayTeamScore: parseInt(e.target.value) || 0
                                            })}
                                            disabled={!onResultsUpdate}
                                        />
                                    </div>
                                    <div style={{ marginTop: '1em' }}>
                                        <Label>Casualties:</Label>
                                        <TableCasInput
                                            type="number"
                                            min="0"
                                            max="10"
                                            value={resultsFormData.awayTeamCasInflicted}
                                            onChange={(e) => setResultsFormData({
                                                ...resultsFormData,
                                                awayTeamCasInflicted: parseInt(e.target.value) || 0
                                            })}
                                            disabled={!onResultsUpdate}
                                        />
                                    </div>
                                </TableTeamColumn>
                                <TableVersusText>@</TableVersusText>
                                <TableTeamColumn>
                                    <TableTeamName>{homeDisplayName}</TableTeamName>
                                    <div>
                                        <Label>Score:</Label>
                                        <TableScoreInput
                                            type="number"
                                            min="0"
                                            max="10"
                                            value={resultsFormData.homeTeamScore}
                                            onChange={(e) => setResultsFormData({
                                                ...resultsFormData,
                                                homeTeamScore: parseInt(e.target.value) || 0
                                            })}
                                            disabled={!onResultsUpdate}
                                        />
                                    </div>
                                    <div style={{ marginTop: '1em' }}>
                                        <Label>Casualties:</Label>
                                        <TableCasInput
                                            type="number"
                                            min="0"
                                            max="10"
                                            value={resultsFormData.homeTeamCasInflicted}
                                            onChange={(e) => setResultsFormData({
                                                ...resultsFormData,
                                                homeTeamCasInflicted: parseInt(e.target.value) || 0
                                            })}
                                            disabled={!onResultsUpdate}
                                        />
                                    </div>
                                </TableTeamColumn>
                            </TableResultsFormGrid>
                            
                            {onResultsUpdate && (
                                <TableOvertimeSection>
                                    <TableOvertimeCheckbox
                                        type="checkbox"
                                        id="table-overtime"
                                        checked={resultsFormData.overtime}
                                        onChange={(e) => setResultsFormData({
                                            ...resultsFormData,
                                            overtime: e.target.checked
                                        })}
                                    />
                                    <TableOvertimeLabel htmlFor="table-overtime">
                                        Overtime Game
                                    </TableOvertimeLabel>
                                </TableOvertimeSection>
                            )}
                            
                            {!onResultsUpdate && match.results?.overtime && (
                                <TableResultsMetadata style={{ marginTop: '1.5em' }}>
                                    <TableResultsMetadataText>
                                        <span><strong>Overtime Game:</strong> Yes</span>
                                    </TableResultsMetadataText>
                                </TableResultsMetadata>
                            )}
                            
                            {/* All Statistics (read-only display) */}
                            {match.results && (() => {
                                // Collect all available statistics - check if property exists in the object
                                // Note: 0 is a valid value, so we check for property existence using 'in' operator
                                const stats: Array<{ label: string; home: number | undefined; away: number | undefined }> = [];
                                const results = match.results;
                                
                                // Helper to check if a property exists in the results object
                                const hasProperty = (propName: string) => {
                                    return propName in results;
                                };
                                
                                // Helper to get value safely
                                const getValue = (propName: string): number | undefined => {
                                    return hasProperty(propName) ? (results as any)[propName] : undefined;
                                };
                                
                                // Always show kills if the property exists (even if 0)
                                if (hasProperty('homeTeamKills') || hasProperty('awayTeamKills')) {
                                    stats.push({
                                        label: 'Kills',
                                        home: getValue('homeTeamKills'),
                                        away: getValue('awayTeamKills')
                                    });
                                }
                                
                                // Show all additional statistics if they exist
                                if (hasProperty('homeTeamTV') || hasProperty('awayTeamTV')) {
                                    stats.push({
                                        label: 'TV',
                                        home: getValue('homeTeamTV'),
                                        away: getValue('awayTeamTV')
                                    });
                                }
                                
                                if (hasProperty('homeTeamBlocks') || hasProperty('awayTeamBlocks')) {
                                    stats.push({
                                        label: 'Blocks',
                                        home: getValue('homeTeamBlocks'),
                                        away: getValue('awayTeamBlocks')
                                    });
                                }
                                
                                if (hasProperty('homeTeamAvBr') || hasProperty('awayTeamAvBr')) {
                                    stats.push({
                                        label: 'AvBr',
                                        home: getValue('homeTeamAvBr'),
                                        away: getValue('awayTeamAvBr')
                                    });
                                }
                                
                                if (hasProperty('homeTeamKO') || hasProperty('awayTeamKO')) {
                                    stats.push({
                                        label: 'KO',
                                        home: getValue('homeTeamKO'),
                                        away: getValue('awayTeamKO')
                                    });
                                }
                                
                                if (hasProperty('homeTeamSurfs') || hasProperty('awayTeamSurfs')) {
                                    stats.push({
                                        label: 'Surfs',
                                        home: getValue('homeTeamSurfs'),
                                        away: getValue('awayTeamSurfs')
                                    });
                                }
                                
                                if (hasProperty('homeTeamFouls') || hasProperty('awayTeamFouls')) {
                                    stats.push({
                                        label: 'Fouls',
                                        home: getValue('homeTeamFouls'),
                                        away: getValue('awayTeamFouls')
                                    });
                                }
                                
                                if (hasProperty('homeTeamPasses') || hasProperty('awayTeamPasses')) {
                                    stats.push({
                                        label: 'Passes',
                                        home: getValue('homeTeamPasses'),
                                        away: getValue('awayTeamPasses')
                                    });
                                }
                                
                                if (hasProperty('homeTeamSPP') || hasProperty('awayTeamSPP')) {
                                    stats.push({
                                        label: 'SPP',
                                        home: getValue('homeTeamSPP'),
                                        away: getValue('awayTeamSPP')
                                    });
                                }

                                console.log("Here's the stats:", stats);
                                
                                // Only render if we have stats to show
                                if (stats.length === 0) {
                                    return null;
                                }

                                return (
                                    <TableResultsMetadata style={{ marginTop: '1.5em' }}>
                                        <TableResultsMetadataLabel>Match Statistics:</TableResultsMetadataLabel>
                                        <TableResultsMetadataText>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '0.5em', marginTop: '0.5em' }}>
                                                <div style={{ textAlign: 'center', fontWeight: 600, color: '#495057' }}>{awayDisplayName}</div>
                                                <div style={{ textAlign: 'center', fontWeight: 600, color: '#495057' }}>Stat</div>
                                                <div style={{ textAlign: 'center', fontWeight: 600, color: '#495057' }}>{homeDisplayName}</div>
                                                
                                                {stats.map((stat, index) => (
                                                    <React.Fragment key={index}>
                                                        <div style={{ textAlign: 'center' }}>{stat.away !== undefined ? stat.away : '-'}</div>
                                                        <div style={{ textAlign: 'center', fontWeight: 500 }}>{stat.label}</div>
                                                        <div style={{ textAlign: 'center' }}>{stat.home !== undefined ? stat.home : '-'}</div>
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                        </TableResultsMetadataText>
                                    </TableResultsMetadata>
                                );
                            })()}
                            
                            {match.results && (match.results.source || match.results.createdAt || match.results.updatedAt) && (
                                <TableResultsMetadata>
                                    <TableResultsMetadataLabel>Result Information:</TableResultsMetadataLabel>
                                    <TableResultsMetadataText>
                                        {match.results.source && (
                                            <span>
                                                <strong>Source:</strong> {
                                                    match.results.source === 'manual' 
                                                        ? 'Manually entered' 
                                                        : match.results.source.startsWith('discord:')
                                                        ? `Discord (${match.results.source.replace('discord:', '')})`
                                                        : match.results.source.startsWith('nuffle:')
                                                        ? `Nuffle (${match.results.source.replace('nuffle:', '')})`
                                                        : match.results.source
                                                }
                                            </span>
                                        )}
                                        {match.results.createdAt && (
                                            <span>
                                                <strong>Added:</strong> {new Date(match.results.createdAt).toLocaleString()}
                                            </span>
                                        )}
                                        {match.results.updatedAt && match.results.updatedAt !== match.results.createdAt && (
                                            <span>
                                                <strong>Last updated:</strong> {new Date(match.results.updatedAt).toLocaleString()}
                                            </span>
                                        )}
                                    </TableResultsMetadataText>
                                </TableResultsMetadata>
                            )}
                            
                            {onResultsUpdate && (
                                <TableResultsActions>
                                    <TableCancelButton onClick={handleCancelTableResults}>
                                        Cancel
                                    </TableCancelButton>
                                    <TableResultsButton onClick={handleSaveTableResults}>
                                        Save Results
                                    </TableResultsButton>
                                </TableResultsActions>
                            )}
                            
                            {!onResultsUpdate && (
                                <TableResultsActions>
                                    <TableCancelButton onClick={handleCancelTableResults}>
                                        Close
                                    </TableCancelButton>
                                </TableResultsActions>
                            )}
                        </TableResultsModalContent>
                    </TableResultsModal>
                    );
                } catch (error) {
                    console.error('Error rendering results modal:', error);
                    return null;
                }
            })()}
        </ScheduleTableSection>
    );
}; 